'use client';

/**
 * MegaMenu.tsx — toppmeny med mega-meny for lg+ (ibm.com masthead)
 *
 * Mønster: WAI-ARIA «Disclosure Navigation» — toppvalg med meny er <button
 * aria-expanded aria-controls>, panelet er vanlig innhold med lenker (ikke
 * menubar). Åpnes ved klikk; lukkes med Escape (fokus tilbake til knappen),
 * klikk utenfor, fokus ut av menyen eller navigasjon. Panelet er et fullbredde
 * panel under headeren med «scrim» over resten av siden.
 *
 * Kategorier (venstre) velges med klikk/Enter/hover, og med pil opp/ned/Home/End.
 * Innholdet (MastheadMenu) kommer fra masthead-content.ts — ingen ruter i koden.
 */

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, ChevronDown, ChevronUp } from '@carbon/icons-react';
import type { MastheadCategory, MastheadMenu } from '@/types/landing';
import LandingButton from './LandingButton';
import { LANDING_ICONS } from './landing-icons';
import styles from './mega-menu.module.css';

interface MegaMenuProps {
  menu: MastheadMenu;
  navLabel: string;
}

/** Bare path-delen av en lenke («/#feature-crm» → «/»), for aria-current */
function pathOf(href: string): string {
  return href.split('#')[0] || '/';
}

export default function MegaMenu({ menu, navLabel }: MegaMenuProps) {
  const pathname = usePathname();
  const uid = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const catRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Åpen meny er knyttet til siden den ble åpnet på: navigasjon lukker den uten effekt
  const [state, setState] = useState<{ id: string | null; path: string }>({ id: null, path: '' });
  const openId = state.path === pathname ? state.id : null;
  const [activeCat, setActiveCat] = useState<string>('');

  const openEntry = menu.entries.find((e) => e.kind === 'mega' && e.id === openId);
  const categories: MastheadCategory[] = openEntry && openEntry.kind === 'mega' ? openEntry.categories : [];
  const category = categories.find((c) => c.id === activeCat) ?? categories[0];

  const close = useCallback(
    (returnFocus = false) => {
      if (returnFocus && openId) triggerRefs.current[openId]?.focus();
      setState({ id: null, path: pathname });
    },
    [openId, pathname],
  );

  // Escape, klikk utenfor og fokus ut lukker menyen mens den er åpen
  useEffect(() => {
    if (!openId) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
    };
    const onPointer = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [openId, close]);

  const toggle = (entryId: string, firstCat: string) => {
    if (openId === entryId) {
      close();
      return;
    }
    setActiveCat(firstCat);
    setState({ id: entryId, path: pathname });
  };

  const onCatKey = (e: KeyboardEvent<HTMLUListElement>) => {
    const ids = categories.map((c) => c.id);
    const i = ids.indexOf(category?.id ?? '');
    let next = -1;
    if (e.key === 'ArrowDown') next = (i + 1) % ids.length;
    else if (e.key === 'ArrowUp') next = (i - 1 + ids.length) % ids.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = ids.length - 1;
    if (next < 0) return;
    e.preventDefault();
    setActiveCat(ids[next]);
    catRefs.current[ids[next]]?.focus();
  };

  return (
    <div
      ref={rootRef}
      className={styles.root}
      onBlur={(e) => {
        // Fokus forlot hele menyen (nav + panel)
        if (openId && !rootRef.current?.contains(e.relatedTarget as Node | null) && e.relatedTarget) close();
      }}
    >
      <nav className={styles.nav} aria-label={navLabel}>
        <ul className={styles.list}>
          {menu.entries.map((entry) => {
            if (entry.kind === 'link') {
              const current = pathOf(entry.href) === pathname && !entry.href.includes('#');
              return (
                <li key={entry.id}>
                  <Link
                    href={entry.href}
                    className={styles.item}
                    aria-current={current ? 'page' : undefined}
                    onClick={() => close()}
                  >
                    {entry.label}
                  </Link>
                </li>
              );
            }
            const expanded = openId === entry.id;
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  ref={(el) => {
                    triggerRefs.current[entry.id] = el;
                  }}
                  className={`${styles.item} ${styles.trigger} ${expanded ? styles.expanded : ''}`}
                  aria-expanded={expanded}
                  aria-controls={`${uid}-${entry.id}`}
                  onClick={() => toggle(entry.id, entry.categories[0]?.id ?? '')}
                >
                  {entry.label}
                  {expanded ? <ChevronUp size={16} aria-hidden /> : <ChevronDown size={16} aria-hidden />}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {openEntry && openEntry.kind === 'mega' && category && (
        <>
          {/* Scrim: dimmer resten av siden; klikk lukker */}
          <div className={styles.scrim} aria-hidden onClick={() => close()} />

          <div id={`${uid}-${openEntry.id}`} className={styles.panel}>
            <div className={styles.panelInner}>
              <div className={styles.left}>
                <ul className={styles.cats} aria-label={menu.labels.categories} onKeyDown={onCatKey}>
                  {categories.map((c) => {
                    const selected = c.id === category.id;
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          ref={(el) => {
                            catRefs.current[c.id] = el;
                          }}
                          className={`${styles.cat} ${selected ? styles.catSelected : ''}`}
                          aria-current={selected ? 'true' : undefined}
                          tabIndex={selected ? 0 : -1}
                          onClick={() => setActiveCat(c.id)}
                          onMouseEnter={() => setActiveCat(c.id)}
                          onFocus={() => setActiveCat(c.id)}
                        >
                          {c.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <div className={styles.all}>
                  <LandingButton link={openEntry.all} kind="primary" />
                </div>
              </div>

              <div className={styles.right}>
                <div className={styles.main}>
                  {category.headingHref ? (
                    <Link href={category.headingHref} className={styles.headingLink} onClick={() => close()}>
                      {category.heading}
                      <ArrowRight size={24} aria-hidden />
                    </Link>
                  ) : (
                    <p className={styles.heading}>{category.heading}</p>
                  )}
                  <p className={styles.intro}>{category.intro}</p>

                  <p className={styles.label}>{category.itemsLabel ?? menu.labels.products}</p>
                  <ul className={styles.rows}>
                    {category.items.map((item) => {
                      const Icon = LANDING_ICONS[item.icon];
                      return (
                        <li key={item.href + item.label} className={styles.rowItem}>
                          <Link href={item.href} className={styles.row} onClick={() => close()}>
                            <Icon className={styles.rowIcon} aria-hidden />
                            <span className={styles.rowText}>
                              <span className={styles.rowName}>
                                {item.label}
                                {item.status && <span className={styles.tag}>{item.status}</span>}
                              </span>
                              <span className={styles.rowDesc}>{item.description}</span>
                            </span>
                            <ArrowRight size={20} aria-hidden className={styles.rowArrow} />
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {category.links.length > 0 && (
                  <div className={styles.strip}>
                    <span className={styles.stripLabel}>{menu.labels.shortcuts}</span>
                    <ul className={styles.stripLinks}>
                      {category.links.map((link) => (
                        <li key={link.href}>
                          <Link href={link.href} className={styles.stripLink} onClick={() => close()}>
                            {link.label}
                            <ArrowRight size={16} aria-hidden />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
